import { useCallback, useEffect, useImperativeHandle, useRef, useState, type Ref } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

export interface SignaturePadHandle {
  /** Trimmed PNG (data URL) and the raw strokes [[x, y, ms], ...], or null when empty. */
  export: () => { png: string; points: number[][][] } | null
  clear: () => void
}

interface Props {
  ref?: Ref<SignaturePadHandle>
  onInkChange?: (hasInk: boolean) => void
  className?: string
  /** Most of the screen height (phone page). */
  tall?: boolean
  /** Height of the drawing area, when neither size fits. */
  padClassName?: string
}

const INK = '#0A192F'
const LINE_WIDTH = 2.4

/**
 * Finger or mouse signature. Pointer events cover touch, pen and mouse;
 * `touch-action: none` keeps the page from scrolling while signing on a phone.
 */
export function SignaturePad({ ref, onInkChange, className, tall = false, padClassName }: Props) {
  const { t } = useTranslation()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const strokes = useRef<number[][][]>([])
  const drawing = useRef(false)
  const start = useRef(0)
  const [hasInk, setHasInk] = useState(false)

  const setInk = useCallback(
    (value: boolean) => {
      setHasInk(value)
      onInkChange?.(value)
    },
    [onInkChange],
  )

  const redraw = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const ratio = window.devicePixelRatio || 1
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.strokeStyle = INK
    ctx.lineWidth = LINE_WIDTH
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    for (const stroke of strokes.current) {
      ctx.beginPath()
      stroke.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)))
      if (stroke.length === 1) ctx.lineTo(stroke[0][0] + 0.1, stroke[0][1] + 0.1)
      ctx.stroke()
    }
  }, [])

  // Canvas sized to its box in device pixels, so the line stays sharp.
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const resize = () => {
      const ratio = window.devicePixelRatio || 1
      const { width, height } = canvas.getBoundingClientRect()
      canvas.width = Math.round(width * ratio)
      canvas.height = Math.round(height * ratio)
      redraw()
    }
    resize()
    const observer = new ResizeObserver(resize)
    observer.observe(canvas)
    return () => observer.disconnect()
  }, [redraw])

  const point = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    return [
      Math.round((e.clientX - rect.left) * 10) / 10,
      Math.round((e.clientY - rect.top) * 10) / 10,
      Math.round(performance.now() - start.current),
    ]
  }

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault()
    e.currentTarget.setPointerCapture(e.pointerId)
    if (strokes.current.length === 0) start.current = performance.now()
    drawing.current = true
    strokes.current.push([point(e)])
    redraw()
  }

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return
    strokes.current[strokes.current.length - 1].push(point(e))
    redraw()
  }

  const onPointerUp = () => {
    if (!drawing.current) return
    drawing.current = false
    const ink = strokes.current.reduce((n, s) => n + s.length, 0) >= 8
    if (ink !== hasInk) setInk(ink)
  }

  const clear = useCallback(() => {
    strokes.current = []
    redraw()
    setInk(false)
  }, [redraw, setInk])

  useImperativeHandle(ref, () => ({
    clear,
    export: () => {
      const canvas = canvasRef.current
      if (!canvas || !hasInk) return null
      // Crop to the ink, with a small margin, at device resolution.
      const ratio = window.devicePixelRatio || 1
      const all = strokes.current.flat()
      const pad = 6
      const minX = Math.max(0, Math.min(...all.map((p) => p[0])) - pad)
      const minY = Math.max(0, Math.min(...all.map((p) => p[1])) - pad)
      const maxX = Math.max(...all.map((p) => p[0])) + pad
      const maxY = Math.max(...all.map((p) => p[1])) + pad
      const out = document.createElement('canvas')
      out.width = Math.max(1, Math.round((maxX - minX) * ratio))
      out.height = Math.max(1, Math.round((maxY - minY) * ratio))
      out
        .getContext('2d')
        ?.drawImage(canvas, minX * ratio, minY * ratio, out.width, out.height, 0, 0, out.width, out.height)
      return { png: out.toDataURL('image/png'), points: strokes.current }
    },
  }))

  return (
    <div className={cn('space-y-2', className)}>
      <div className="relative rounded-lg border border-slate-300 dark:border-slate-700 bg-white">
        <canvas
          ref={canvasRef}
          aria-label={t('manager.numbers.port.sign.padLabel')}
          className={cn(
            'block w-full touch-none cursor-crosshair rounded-lg',
            tall ? 'h-[calc(100dvh-25rem)] min-h-[14rem]' : 'h-40 sm:h-36',
            padClassName,
          )}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        />
        <div className="pointer-events-none absolute left-4 right-4 bottom-8 border-b border-dashed border-slate-300" />
        {!hasInk && (
          <span className="pointer-events-none absolute inset-x-0 bottom-2 text-center text-xs text-slate-400">
            {t('manager.numbers.port.sign.padHint')}
          </span>
        )}
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={clear}
          disabled={!hasInk}
          className="text-sm font-medium text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white disabled:opacity-40"
        >
          {t('manager.numbers.port.sign.clear')}
        </button>
      </div>
    </div>
  )
}
